import React from 'react';
import DropzoneComponent from 'react-dropzone-component';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { PropTypes } from 'prop-types';
import { connect } from 'react-redux';
import ReactDOMServer from 'react-dom/server';
import { injectIntl } from 'react-intl';

import { getStorageSetting } from 'common/storage';
import { history } from 'containers/App';

import LocalisedMessage from 'components/Common/LocalisedMessage';

class FileUpload extends React.Component {
  static propTypes = {
    postUrl: PropTypes.string.isRequired,
    docCategory: PropTypes.string.isRequired,
    docType: PropTypes.string.isRequired,
    linkType: PropTypes.string,
    id: PropTypes.number,
    acceptedFiles: PropTypes.string,
    removeFileAfterUpload: PropTypes.bool,
    defaultMessage: PropTypes.string,
    onFileQueued: PropTypes.func,
    onFileRemoved: PropTypes.func,
    onFileUploadCancel: PropTypes.func,
    onFileUploadSuccess: PropTypes.func,
    onFileUploadError: PropTypes.func,
    uploadFileSettings: PropTypes.shape(
      {
        uploadTimeout: PropTypes.number,
        maxUploadFiles: PropTypes.number,
        maxFileSize: PropTypes.number,
        maxParallelUploads: PropTypes.number
      }
    ),
    intl: PropTypes.shape({
      formatMessage: PropTypes.func.isRequired
    }).isRequired,
    locale: PropTypes.string.isRequired,
    isQuestionnaireUpload: PropTypes.bool,
    uploadTitle: PropTypes.string,
    allowMultipleFiles: PropTypes.bool,
    previewClassName: PropTypes.string
  };

  static defaultProps = {
    defaultMessage: 'Drop files here to upload',
    linkType: 'form',
    id: null,
    removeFileAfterUpload: false,
    onFileQueued: null,
    onFileRemoved: null,
    onFileUploadCancel: null,
    onFileUploadSuccess: null,
    onFileUploadError: null,
    acceptedFiles: null,
    uploadFileSettings: null,
    isQuestionnaireUpload: false,
    uploadTitle: 'fileUpload.title',
    allowMultipleFiles: true,
    previewClassName: '',
  };

  static css(el, styles) {
    const mergedStyles = Object.keys(styles) != null && Object.keys(styles)
      .map(k => k != null && styles[k]).join(',');
    el.setAttribute('style', mergedStyles);
  }

  constructor(props) {
    super(props);

    const { uploadFileSettings, allowMultipleFiles } = this.props;

    this.state = {
      timeout: uploadFileSettings != null ? uploadFileSettings.uploadTimeout : 60000,
      maxUploadFiles: uploadFileSettings != null ? uploadFileSettings.maxUploadFiles : 1000,
      maxFilesize: uploadFileSettings != null ? uploadFileSettings.maxFileSize : 5,
      maxParellelUploads: uploadFileSettings != null ? uploadFileSettings.maxParallelUploads : 2
    };

    if (!allowMultipleFiles) {
      this.state.maxUploadFiles = 1;
      this.state.maxParellelUploads = 1;
    }

    this.initCallback = this.initCallback.bind(this);
    this.onTimeout = this.onTimeout.bind(this);
  }

  onAddedFile(file) {
    const { onFileQueued } = this.props;
    const { filesToRejectCount } = this.state;

    if (filesToRejectCount) {
      this.dropzone.removeAllFiles();
      this.state.filesToRejectCount -= 1;
      return;
    }

    if (file.accepted === true) {
      if (onFileQueued) {
        onFileQueued(file);
      }
    }
  }

  onRemovedFile(file) {
    const { onFileRemoved } = this.props;

    if (onFileRemoved) {
      onFileRemoved(file);
    }
  }

  onCancel(file) {
    const { onFileUploadCancel } = this.props;

    if (file.accepted === true && file.status === 'canceled') {
      if (onFileUploadCancel) {
        onFileUploadCancel(file);
      }
    }
  }

  onSuccess(file) {
    const { removeFileAfterUpload } = this.props;

    if (removeFileAfterUpload) {
      this.dropzone.removeFile(file);
    }
  }

  onComplete(file) {
    const { onFileUploadSuccess, onFileUploadError, intl } = this.props;

    if (file.accepted === true && file.status === 'success') {
      if (file.xhr && file.xhr.response === '[]') {
        if (onFileUploadError) {
          onFileUploadError(file, { code: 400, message: intl.formatMessage({ id: 'fileUpload.problemText' }) });
        }
      }

      if (onFileUploadSuccess) {
        onFileUploadSuccess(file, JSON.parse(file.xhr.response));
      }
    }
  }

  onError(file, response) {
    const { onFileUploadError, intl } = this.props;
    const HTTP_498_SESSIONTIMEOUT = 498;

    if (file.status === 'error' && (file.accepted === false || (file.accepted === true && response))) {
      if (onFileUploadError) {
        try {
          const jsonResponse = JSON.parse(response);
          if (jsonResponse?.code === HTTP_498_SESSIONTIMEOUT) {
            history.push('/sessiontimeout');
          } else {
            const errorResponse = (jsonResponse?.code && jsonResponse?.message) ? jsonResponse : { code: 400, message: intl.formatMessage({ id: 'fileUpload.problemText' }) };
            onFileUploadError(file, errorResponse);
          }
        } catch (e) {
          onFileUploadError(file, response.error ? response.error : { code: 400, message: intl.formatMessage({ id: 'fileUpload.problemText' }) });
        }
      }
      if (this.dropzone) {
        this.dropzone.removeFile(file);
      }
    }
  }

  onSending(file, xhr, formData) {
    const timeoutHandler = this.onTimeout;

    xhr.ontimeout = function (e) {
      timeoutHandler(file, xhr, formData, e);
    };
  }

  onTimeout(file, xhr, formData, e) {
    const { onFileUploadError, intl } = this.props;

    if (onFileUploadError) {
      onFileUploadError(file, { code: 504, message: `${ intl.formatMessage({ id: 'fileUpload.timeout' }, { FileName: file.name }) }` });
    }

    if (this.dropzone) {
      this.dropzone.removeFile(file);
    }
  }

  onDrop(event) {
    const { allowMultipleFiles } = this.props;

    const files = event?.dataTransfer?.files;
    if (!files || allowMultipleFiles) return;
    if (files.length > 1) {
      this.state.filesToRejectCount = files.length;
    }
  }

  initCallback(dropzone) {
    this.dropzone = dropzone;

    dropzone.on('success', (file) => {
      const els = document.querySelector('.dz-success-mark span');
      if (els) {
        FileUpload.css(document.querySelector('.dz-success-mark span'), { color: 'green' });
        FileUpload.css(document.querySelector('.dz-error-mark'), { display: 'none' });
      }
    });
    dropzone.on('error', (file) => {
      const els = document.querySelector('.dz-error-mark span');
      if (els) {
        FileUpload.css(document.querySelector('.dz-error-mark span'), { color: 'red' });
        FileUpload.css(document.querySelector('.dz-success-mark'), { display: 'none' });
      }
    });
  }

  render() {
    const { postUrl, docCategory, docType, linkType, id, acceptedFiles, defaultMessage, intl, locale, isQuestionnaireUpload, uploadTitle, previewClassName } = this.props;
    const { timeout, maxUploadFiles, maxFilesize, maxParellelUploads } = this.state;

    const componentConfig = {
      postUrl
    };
    const selectedClient = getStorageSetting('selectedClient');
    const headers = { Authorization: null, 'Accept-Language': locale };
    if (selectedClient != null) {
      headers['TT-CLIENT'] = selectedClient.key;
    }
    const djsConfig = {
      withCredentials: true,
      addRemoveLinks: true,
      autoProcessQueue: true,
      maxFiles: maxUploadFiles > 0 ? maxUploadFiles : null,
      timeout,
      acceptedFiles,
      maxFilesize,
      parallelUploads: maxParellelUploads,
      dictDefaultMessage: defaultMessage,
      method: 'put',
      params: {
        category: docCategory,
        type: docType
      },
      previewTemplate: ReactDOMServer.renderToStaticMarkup(
        <div className={ `dz-preview dz-file-preview ${ previewClassName }` }>
          <div className="dz-details">
            <div className="dz-filename"><span data-dz-name="true" /></div>
          </div>
          <div className="dz-progress"><div className="dz-upload" data-dz-uploadprogress="true" /></div>
          <div className="dz-success-mark"><span>&#x2714;</span></div>
          <div className="dz-error-mark"><span>&#x2718;</span></div>
          <div className="dz-error-message"><span data-dz-errormessage="true" /></div>
        </div>
      ),
      headers,
      dictCancelUpload: intl.formatMessage({ id: 'supportingDocuments.cancelUpload' }),
      dictCancelUploadConfirmation: intl.formatMessage({ id: 'supportingDocuments.cancelUploadConfirmation' }),
      dictRemoveFile: intl.formatMessage({ id: 'supportingDocuments.removeFile' }),
    };

    if (id) {
      djsConfig.params.linkType = linkType;
      djsConfig.params.id = id;
    }

    const eventHandlers = {
      init: this.initCallback,
      addedfile: file => this.onAddedFile(file),
      removedfile: file => this.onRemovedFile(file),
      complete: file => this.onComplete(file),
      success: file => this.onSuccess(file),
      canceled: file => this.onCancel(file),
      error: (file, errorMessage) => this.onError(file, errorMessage),
      sending: (file, xhr, formData) => this.onSending(file, xhr, formData),
      drop: event => this.onDrop(event)
    };
    return (
      <React.Fragment>
        { docCategory && docType && <DropzoneComponent
          config={ componentConfig }
          eventHandlers={ eventHandlers }
          djsConfig={ djsConfig }
        >
          <div className={ `${ isQuestionnaireUpload ? 'dz-questionnaire' : 'dz-default' } dz-message` }>
            <div className={ isQuestionnaireUpload ? '' : 'row' }>
              <div className={ isQuestionnaireUpload ? '' : 'col' }>
                <button type="button" className={ `${ isQuestionnaireUpload ? 'questionnaire-btn' : '' } dz-upload-btn` }>
                  <FontAwesomeIcon className="upload-icon" icon="upload" />
                  <span><LocalisedMessage id={ uploadTitle } /></span>
                </button>
              </div>
            </div>
          </div>
        </DropzoneComponent> }
        { (!docCategory || !docType) && <div className="filepicker dropzone dz-disabled">
          <div className={ `${ isQuestionnaireUpload ? 'dz-questionnaire' : 'dz-default' } dz-message` }>
            <div className={ isQuestionnaireUpload ? '' : 'row' }>
              <div className={ isQuestionnaireUpload ? '' : 'col' }>
                <FontAwesomeIcon className="upload-icon" icon="upload" />
                <p><LocalisedMessage id="fileUpload.title" /></p>
              </div>
            </div>
          </div>
        </div> }
      </React.Fragment>
    );
  }
}

function mapStateToProps(state) {
  const {
    app: {
      onlineConfig
    }
  } = state;

  return {
    uploadFileSettings: onlineConfig && onlineConfig.uploadFileSettings
  };
}
export default connect(
  mapStateToProps
)(injectIntl(FileUpload));
